<template>
  <v-container class="physical-activity-progress-status">
    <v-row>
      <v-col>
        <v-row class="mx-5 my-2">
          <v-col cols="12">
            <h3 class="py-5">{{ $ucfirst($t("pa.currentPhysicalActivity")) }}</h3>
          </v-col>
        </v-row>
        <v-row class="px-5 py-7" v-if="numberOfDaysWithActivityReportedDate">
          <v-col cols="12" class="border">
            <v-row align="center">
              <v-col cols="1" align="center">
                <smart-icon
                  :icon="numberOfDaysWithActivityIconName"
                />
              </v-col>
              <!-- <v-col cols="8">{{ numberOfDaysWithActivity }} days with a total of 30 minutes or more of at least moderate physical activity</v-col> -->
              <v-col cols="8">{{ $ucfirst($t("pa.numberOfDaysWithModeratePA", { params: { numberOfDaysWithActivity } })) }}</v-col>
              <v-col cols="3">
                <p class="physical-activity-progress-status__reported-on">{{ $ucfirst($t("pa.reportedOn")) }} {{ numberOfDaysWithActivityReportedDate }}</p>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="px-5 py-7" v-if="RAPAReportedDate">
          <v-col cols="12" class="border">
            <v-row>
              <v-spacer />
              <v-col cols="3">
                <p class="physical-activity-progress-status__reported-on">
                  {{ $ucfirst($t("pa.reportedOn")) }} {{ RAPAReportedDate }}
                </p>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="1" align="center">
                <smart-icon
                  icon="mdi-run"
                  :iconColor="RAPAAerobicColor"
                />
              </v-col>
              <v-col>
                <span :style="{color: RAPAAerobicColor}">
                  {{ RAPAAerobicLabel }}
                </span>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="1" align="center">
                <smart-icon
                  icon="mdi-dumbbell"
                  :iconColor="RAPAMuscleStrengthColor"
                />
              </v-col>
              <v-col>
                <span :style="{color: RAPAMuscleStrengthColor}">
                  {{ RAPAMuscleStrengthLabel }}
                </span>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="1" align="center">
                <smart-icon
                  icon="mdi-yoga"
                  :iconColor="RAPAFlexibilityColor"
                />
              </v-col>
              <v-col>
                <span :style="{color: RAPAFlexibilityColor}">
                  {{ RAPAFlexibilityLabel }}
                </span>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import SmartIcon from '../../../components/Parts/SmartIcon';
import gql from 'graphql-tag';
import moment from 'moment';
import { mapGetters } from 'vuex';

/**
 * Component PatientOverviewPAProgressStatus.vue
 *
 * @module PatientOverviewPAProgressStatus
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.7] app/src/APP_DASHBOARD/templates/views/Patient/PA/PatientOverviewPAProgressStatus.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "PatientOverviewPAProgressStatus",
  components: {
    SmartIcon
  },
  data: function() {
    return {
      numberOfDaysWithActivity: null,
      numberOfDaysWithActivityReportedDate: null,
      numberOfDaysWithActivityIconName: 'pa_active',
      RAPAReportedDate: null,
      RAPAAerobicLabel: null,
      RAPAAerobicColor: null,
      RAPAFlexibilityLabel: null,
      RAPAFlexibilityColor: null,
      RAPAMuscleStrengthLabel: null,
      RAPAMuscleStrengthColor: null,
    }
  },
  computed: {
    ...mapGetters({
      patientId: 'patientSummary/id',
    }),
  },
  mounted: function(){
    this.getNumberOfDaysWithPhysicalActivity();
    this.getResultsRAPA();
  },
  methods: {
    getNumberOfDaysWithPhysicalActivity() {
      this.$app.apolloMobileClient
        .query({
          query: gql`
            query myQuestionnaireForms(
              $patientId: String!
            ){
              myQuestionnaireForms(
                conditions : {gate : and, inputs : [
                  {field : completed, value : "1"},
                  {field : key, value : "PA"}
                ]},
                orders : {field : updatedAt, direction : desc}
                limit : 1
                 functions: {
                  function: patientId,
                  parameters: [$patientId]
                }
              ){
                questionnaire{
                  key
                }
                id
                completed
                score
                entryDate
                updatedAt
                entries{
                  givenAnswers{
                    openAnswer
                  }
                }
              }
            }
          `,
          variables: {
            patientId: this.patientId
          }
        })
        .then((result) => {
          if (result.data.myQuestionnaireForms[0]?.entries?.[0]){
            this.numberOfDaysWithActivity = parseInt(result.data.myQuestionnaireForms[0].entries[0].givenAnswers[0].openAnswer);
            this.numberOfDaysWithActivityReportedDate = moment(result.data.myQuestionnaireForms[0].entryDate).format('DD/MM/YYYY');
          }else{
            this.numberOfDaysWithActivity = 0;
            this.numberOfDaysWithActivityReportedDate = null;
          }

          this.getIconNameForDaysWithActivity();
        });
    },
    getIconNameForDaysWithActivity() {
      if(this.numberOfDaysWithActivity >= 5){
        this.numberOfDaysWithActivityIconName = 'mdi-emoticon-excited-outline';
      } else if(this.numberOfDaysWithActivity >= 1) {
        this.numberOfDaysWithActivityIconName = 'mdi-emoticon-happy-outline';
      } else {
        this.numberOfDaysWithActivityIconName = 'mdi-emoticon-neutral-outline';
      }
    },
    getResultsRAPA() {
      this.$app.apolloMobileClient
        .query({
          query: gql`
            query myQuestionnaireForms(
              $patientId: String!
            ){
              myQuestionnaireForms(
                functions: {
                  parameters: [$patientId],
                  function: patientId
                },
                conditions: {
                  gate: and,
                  inputs: [
                    {
                      field: completed,
                      value: "1"
                    }, {
                      field: key,
                      value: "RAPA"
                    }
                  ]
                }
              ) {
                questionnaire {
                  key
                  name
                }
                entryDate
                id
                completed
                score
                entryDate
                entries {
                  id
                  question {
                    key
                  }
                  givenAnswers {
                    key
                  }
                }
              }
            }

          `,
          variables: {
            patientId: this.patientId
          }
        })
        .then(result => {
          const data = result.data.myQuestionnaireForms[0];

          if (!data || data == null){
            this.RAPAReportedDate = null;
            this.RAPAAerobicLabel = null;
            this.RAPAAerobicColor = null;
            this.RAPAFlexibilityLabel = null;
            this.RAPAFlexibilityColor = null;
            this.RAPAMuscleStrengthLabel = null;
            this.RAPAMuscleStrengthColor = null;
            return;
          }

          this.RAPAReportedDate = moment(data.entryDate).format('DD/MM/YYYY');

          data.entries.forEach((entry) => {
            switch(entry.question.key) {
              case 'FLEXIBILITY_ACTIVITIES':
                if(entry.givenAnswers[0].key === "A_NO") {
                  this.RAPAFlexibilityLabel = this.$t('pa.didNotPerformFlexibilityExercises');
                  this.RAPAFlexibilityColor = this.$color.coro["danger-500"];
                } else {
                  this.RAPAFlexibilityLabel = this.$t('pa.fulfilledFlexibilityExercises');
                  this.RAPAFlexibilityColor = this.$color.coro["success-500"];
                }
                break;
              case 'MUSCLE_STRENGTH_ACTIVITIES':
                if(entry.givenAnswers[0].key === "A_NO") {
                  this.RAPAMuscleStrengthLabel = this.$t('pa.didNotPerformStrenthExercises');
                  this.RAPAMuscleStrengthColor = this.$color.coro["danger-500"];
                } else {
                  this.RAPAMuscleStrengthLabel = this.$t('pa.fulfilledStrengthExercises');
                  this.RAPAMuscleStrengthColor = this.$color.coro["success-500"];
                }
                break;
              case "NEVER":
                  this.RAPAAerobicLabel = this.$t('pa.sedentary');
                  this.RAPAAerobicColor = this.$color.coro["danger-500"];
                break;
              case "LIGHT_NOT_EVERY_WEEK":
                if(entry.givenAnswers[0].key === "A_YES" ) {
                  this.RAPAAerobicLabel = this.$t('pa.underActive')
                  this.RAPAAerobicColor = this.$color.coro["danger-500"];
                }
                break;
              case "LIGHT_EVERY_WEEK":
                if(entry.givenAnswers[0].key === "A_YES" ) {
                  this.RAPAAerobicLabel =  this.$t('pa.underActiveRegular', { params: { lightActivities: this.$t('pa.lightActivities') } })
                  this.RAPAAerobicColor = this.$color.coro["danger-500"];
                }
                break;
              case "MODERATE_WEEK_LESS_30":
                if(entry.givenAnswers[0].key === "A_YES" ) {
                  this.RAPAAerobicLabel = this.$t('pa.underActiveRegular', { params: { lightActivities: '' } })
                  this.RAPAAerobicColor = this.$color.coro["warning-500"];
                }
                break;
              case "VIGOROUS_ACTIVITIES_LESS_20":
                if(entry.givenAnswers[0].key === "A_YES" ) {
                  this.RAPAAerobicLabel = this.$t('pa.underActiveRegular', { params: { lightActivities: '' } })
                  this.RAPAAerobicColor = this.$color.coro["warning-500"];
                }
                break;
              case "THIRTY_MINUTES_MODERATE":
                if(entry.givenAnswers[0].key === "A_YES" ) {
                  this.RAPAAerobicLabel = this.$t('pa.active');
                  this.RAPAAerobicColor = this.$color.coro["success-500"];
                }
                break;
              case "TWENTY_MINUTES_VIGOROUS":
                if(entry.givenAnswers[0].key === "A_YES" ) {
                  this.RAPAAerobicLabel = this.$t('pa.active');
                  this.RAPAAerobicColor = this.$color.coro["success-500"];
                }
                break;
              default:
                this.RAPAAerobicLabel = this.$t('pa.sedentary');
                this.RAPAAerobicColor = this.$color.coro["danger-500"];
                break;
            }
          })

        })
    }
  }
};
</script>

<style lang="scss">
.physical-activity-progress-status__reported-on {
  font-size: 0.9rem;
}
</style>
